import { ChannelDefinition } from "../transport/types";
import SkybusLive from "../live";

import { PublishOptions, WireMsg, WireType } from "../types";
import SkybusProviderBase, { OnNewHistoryCallback, OnNewLatestCallback } from "./base";
import { debugLog } from "../unified/debugConsoleLog";

/**
 * When multiple requests come in to subscribe to the same channel in a row the last one "wins".
 * We need to keep track of whether any given channel has a pending timeout and if another request
 * comes in for the same channel while a timeout is pending we need to REPLACE the existing
 * timeout with the new one.
 *
 * This behavior is needed to match the behavior in the actual Skybus message dispatch which will
 * only publish the last request to the vehicle. All prior requests in a burst are dropped.
 *
 * Example:
 *
 * request 1: Channel A, time 1 ms
 * request 2: Channel B, time 3 ms,
 * request 3: Channel A, time 7 ms,
 *
 * We need to DROP the timeout for request 1 because only request 3 will actually get published
 * by skybus. Otherwise we get timeout errors on screen from promises that were never actually
 * dispatched to the vehicle.
 */
const PENDING_TIMEOUTS: Map<string, NodeJS.Timeout> = new Map();

export default class SkybusProviderLive extends SkybusProviderBase {
  constructor(readonly skybusLive: SkybusLive) {
    super();
  }

  public subscribe = <D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    onNewLatest: OnNewLatestCallback<D>,
    _onNewHistory: OnNewHistoryCallback<D>
  ): void => {
    return this._subscribeImpl(channel, type, onNewLatest, _onNewHistory);
  };

  private _subscribeImpl = <D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    onNewLatest: OnNewLatestCallback<D>,
    _onNewHistory: OnNewHistoryCallback<D>,
    onSubscriptionAccepted?: () => void
  ): void => {
    const history: D[] = [];
    if (this.channels.has(channel)) {
      throw Error("Stale history found!");
    }
    // @ts-ignore
    this.channels.set(channel, { history, onNewLatest });
    // do actual subscribe
    this.skybusLive.subscribe(
      channel,
      type,
      (_, message) => {
        onNewLatest(message); // send latest to listeners
      },
      onSubscriptionAccepted
    );
  };

  public async genSubscription<D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    onNewLatest: OnNewLatestCallback<D>,
    onNewHistory: OnNewHistoryCallback<D>,
    debugKey?: string
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const existingTimeout = PENDING_TIMEOUTS.get(channel);
      if (existingTimeout) {
        debugLog({
          debugKey,
          resettingTimeoutForChannel: channel,
        });
        clearTimeout(existingTimeout);
      }
      // wait for up to 10 seconds to get a subscription. In case of error we won't hang forever
      const subTimeLimit = setTimeout(() => {
        debugLog({
          debugKey,
          timedOutWaitingOnChannel: channel,
        });
        reject(`Failed to subscribe to ${channel}`);
      }, 10000);
      debugLog({
        debugKey,
        settingTimeoutForChannel: channel,
      });
      PENDING_TIMEOUTS.set(channel, subTimeLimit);
      this._subscribeImpl(channel, type, onNewLatest, onNewHistory, () => {
        debugLog({
          debugKey,
          confirmedSubscriptionFor: channel,
        });
        clearTimeout(subTimeLimit);
        PENDING_TIMEOUTS.delete(channel);
        resolve();
      });
    });
  }

  public unsubscribe = (channel: string): void => {
    this.skybusLive.unsubscribe(channel);
    this.channels.delete(channel);
  };

  public publish = async <D extends WireMsg>(
    channel: string | ChannelDefinition<WireType<D>>,
    msg: D,
    options?: PublishOptions
  ) => {
    return this.skybusLive.connector.publish(channel, msg, options);
  };

  public isConnected = (): boolean => {
    return this.skybusLive.connector.transport.isConnected;
  };

  public addConnectionStatusListener = (callback: (connected: boolean) => void): (() => void) => {
    return this.skybusLive.connector.transport.addConnectionStatusListener(callback);
  };
}
